import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Stage, Layer, Group, Rect, Circle, Ellipse, Line, Text } from "react-konva";
import { Box, IconButton, Tooltip } from "@mui/material";
import { Add, Remove, CenterFocusStrong } from "@mui/icons-material";
import { posicionesSillas, RADIO_ASIENTO, ANCHO_NOMBRE_FILA, limitar } from "../../domain/mapa/geometria";
import { DecorativoFigura } from "../mapa/figuras";
import { COLOR, conAlfa } from "../mapa/estilos";
import { coloresDeTarifas, colorDeUbicacion } from "../../domain/mapa/mapa";

const ESCALA_MIN = 0.05;
const ESCALA_MAX = 5;
// desde este zoom se leen los números de los asientos
const ESCALA_NUMEROS = 1.1;
const MARGEN = 24;


// Asiento: libre (blanco con el color del sector), elegido (relleno) u ocupado (gris)
const Lugar = ({ x, y, color, estado, etiqueta, onElegir }) => {
  const elegido = estado === "elegido";
  const ocupado = estado === "ocupado";
  const fondo = ocupado ? COLOR.vendido : elegido ? color : "#ffffff";
  return (
    <Group
      x={x}
      y={y}
      onClick={onElegir}
      onTap={onElegir}
      listening={Boolean(onElegir)}
      onMouseEnter={(e) => onElegir && (e.target.getStage().container().style.cursor = "pointer")}
      onMouseLeave={(e) => (e.target.getStage().container().style.cursor = "default")}
    >
      <Circle radius={RADIO_ASIENTO} fill={fondo} stroke={ocupado ? COLOR.vendido : color} strokeWidth={2} hitStrokeWidth={6} />
      {etiqueta && (
        <Text
          x={-RADIO_ASIENTO}
          y={-4}
          width={RADIO_ASIENTO * 2}
          align="center"
          text={etiqueta}
          fontSize={etiqueta.length > 2 ? 6 : 8}
          fill={elegido || ocupado ? "#ffffff" : COLOR.titulo}
          listening={false}
        />
      )}
    </Group>
  );
};

const Mesa = ({ mesa, color, detalle, estadoSilla, enteraEstado, onMesa, onSilla }) => {
  const sillas = posicionesSillas(mesa);
  const elegida = enteraEstado === "elegido";
  const ocupada = enteraEstado === "ocupado";
  const relleno = ocupada ? conAlfa(COLOR.vendido, 0.35) : elegida ? color : conAlfa(color, 0.18);
  const borde = { stroke: ocupada ? COLOR.vendido : color, strokeWidth: elegida ? 3 : 1.5 };
  const props = onMesa
    ? {
        onClick: onMesa,
        onTap: onMesa,
        onMouseEnter: (e) => (e.target.getStage().container().style.cursor = "pointer"),
        onMouseLeave: (e) => (e.target.getStage().container().style.cursor = "default"),
      }
    : {};
  return (
    <Group x={mesa.x} y={mesa.y} rotation={mesa.rotacion || 0}>
      <Group {...props} listening={Boolean(onMesa)}>
        {mesa.forma === "rectangular" ? (
          <Rect width={mesa.ancho} height={mesa.alto} cornerRadius={6} fill={relleno} {...borde} />
        ) : (
          <Ellipse x={mesa.ancho / 2} y={mesa.alto / 2} radiusX={mesa.ancho / 2} radiusY={mesa.alto / 2} fill={relleno} {...borde} />
        )}
        <Text
          text={mesa.nombre}
          width={mesa.ancho}
          y={mesa.alto / 2 - 6}
          align="center"
          fontSize={11}
          fontStyle="bold"
          fill={elegida ? "#ffffff" : COLOR.titulo}
          listening={false}
        />
      </Group>
      {sillas.map((p, i) => {
        const silla = mesa.sillas[i];
        const estado = enteraEstado ? (enteraEstado === "libre" ? "libre" : enteraEstado) : estadoSilla(silla);
        return (
          <Lugar
            key={silla.id}
            x={p.x}
            y={p.y}
            color={color}
            estado={estado}
            etiqueta={detalle ? silla.etiqueta : null}
            onElegir={!enteraEstado && onSilla && estado !== "ocupado" ? () => onSilla(silla) : null}
          />
        );
      })}
    </Group>
  );
};

const Sector = ({ sector, color, activo, resaltado, elegidas, onGeneral, detalle, estadoAsiento, mesaEntera, estadoMesa, onAsiento, onMesa }) => {
  const lugares = sector.filas.length > 0 || sector.mesas.length > 0;
  const contorno = {
    fill: conAlfa(color, resaltado ? 0.14 : lugares ? 0.06 : 0.16),
    stroke: color,
    strokeWidth: resaltado ? 3 : 1.5,
    dash: lugares && !resaltado ? [8, 6] : undefined,
  };
  // con entradas elegidas se rellena más; el borde grueso es sólo del resaltado
  if (elegidas > 0) contorno.fill = conAlfa(color, 0.3);
  // sin fondo ni borde: invisible, pero una general se sigue pudiendo tocar
  if (sector.ocultar_fondo && !resaltado && !(elegidas > 0)) {
    contorno.fill = "rgba(255,255,255,0.01)";
    contorno.stroke = undefined;
  }
  // una ubicación general del plano se toca entera para elegir cantidad
  const elegible = !lugares && activo && onGeneral;
  const tocable = elegible
    ? {
        onClick: () => onGeneral(sector),
        onTap: () => onGeneral(sector),
        onMouseEnter: (e) => (e.target.getStage().container().style.cursor = "pointer"),
        onMouseLeave: (e) => (e.target.getStage().container().style.cursor = "default"),
      }
    : { listening: false };
  return (
    <Group x={sector.x} y={sector.y} rotation={sector.rotacion || 0} opacity={activo ? 1 : 0.35}>
      {sector.forma?.length > 2 ? (
        <Line points={sector.forma.flatMap((p) => [p.x, p.y])} closed {...contorno} {...tocable} />
      ) : (
        <Rect width={sector.ancho} height={sector.alto} cornerRadius={8} {...contorno} {...tocable} />
      )}
      {(sector.partes || []).map((p, i) => (
        <Group key={i} x={p.x} y={p.y} rotation={p.rotacion || 0} listening={false}>
          <Rect
            width={p.ancho}
            height={p.alto}
            cornerRadius={8}
            {...contorno}
            {...(p.ocultar_fondo && !resaltado && !(elegidas > 0) ? { fill: "rgba(255,255,255,0.01)", stroke: undefined } : {})}
          />
          <Text
            visible={!p.ocultar_nombre}
            text={sector.nombre}
            width={p.ancho}
            y={6}
            align="center"
            fontSize={15}
            fontStyle="bold"
            fill={COLOR.titulo}
          />
        </Group>
      ))}
      {sector.filas.map((fila) => (
        <React.Fragment key={fila.nombre || "sueltos"}>
          {fila.nombre && fila.asientos.length > 0 && sector.numeracion !== "nada" && (
            <Text
              x={fila.asientos[0].x - RADIO_ASIENTO - ANCHO_NOMBRE_FILA}
              y={fila.asientos[0].y - 6}
              width={ANCHO_NOMBRE_FILA - 4}
              align="right"
              text={fila.nombre}
              fontSize={11}
              fontStyle="bold"
              fill={COLOR.textoSuave}
              listening={false}
            />
          )}
          {fila.asientos.map((a) => {
            const estado = estadoAsiento(a);
            return (
              <Lugar
                key={a.id}
                x={a.x}
                y={a.y}
                color={color}
                estado={estado}
                etiqueta={detalle && !sector.numeracion ? a.etiqueta : null}
                onElegir={activo && estado !== "ocupado" ? () => onAsiento(a, sector) : null}
              />
            );
          })}
        </React.Fragment>
      ))}
      {sector.mesas.map((mesa) => (
        <Mesa
          key={mesa.id}
          mesa={mesa}
          color={color}
          detalle={detalle && !sector.numeracion}
          estadoSilla={estadoAsiento}
          enteraEstado={mesaEntera ? estadoMesa(mesa) : null}
          onMesa={activo && mesaEntera && estadoMesa(mesa) !== "ocupado" ? () => onMesa(mesa, sector) : null}
          onSilla={activo && !mesaEntera ? (silla) => onAsiento(silla, sector) : null}
        />
      ))}
      {sector.decorativos.map((d, i) => (
        <DecorativoFigura key={i} decorativo={d} />
      ))}
      {elegidas > 0 && (
        <Text
          text={elegidas === 1 ? "1 elegida" : `${elegidas} elegidas`}
          width={sector.ancho}
          y={sector.alto / 2 + 10}
          align="center"
          fontSize={12}
          fill={COLOR.titulo}
          listening={false}
        />
      )}
      <Text
        visible={!sector.ocultar_nombre}
        text={sector.nombre}
        width={sector.ancho}
        y={lugares ? 6 : sector.alto / 2 - 8}
        align="center"
        fontSize={15}
        fontStyle="bold"
        fill={COLOR.titulo}
        listening={false}
      />
    </Group>
  );
};

// PlanoCompra muestra el mapa para elegir lugares: se mueve arrastrando, se
// acerca con la rueda, los botones o pellizcando en el celular.
const PlanoCompra = ({ mapa, activos, foco, resaltado, onGeneral, cantidadGeneral, estadoAsiento, esMesaEntera, estadoMesa, onAsiento, onMesa }) => {
  // cada ubicación con el color de su tarifa, igual que en el editor
  const colores = useMemo(() => coloresDeTarifas(mapa.tarifas || []), [mapa.tarifas]);
  const contenedorRef = useRef(null);
  const [tamano, setTamano] = useState({ ancho: 0, alto: 0 });
  const [vista, setVista] = useState(null);
  const pellizco = useRef(null);

  useEffect(() => {
    const el = contenedorRef.current;
    if (!el) return undefined;
    const observador = new ResizeObserver(([entrada]) => {
      setTamano({ ancho: entrada.contentRect.width, alto: entrada.contentRect.height });
    });
    observador.observe(el);
    return () => observador.disconnect();
  }, []);

  const encuadrar = useCallback(
    (caja) => {
    if (!tamano.ancho || !tamano.alto) return;
    const escala = limitar(
      Math.min((tamano.ancho - 2 * MARGEN) / caja.ancho, (tamano.alto - 2 * MARGEN) / caja.alto),
      ESCALA_MIN,
      2
    );
    setVista({
      escala,
      x: tamano.ancho / 2 - (caja.x + caja.ancho / 2) * escala,
      y: tamano.alto / 2 - (caja.y + caja.alto / 2) * escala,
    });
    },
    [tamano]
  );

  const todo = { x: 0, y: 0, ancho: mapa.ancho, alto: mapa.alto };

  useEffect(() => {
    if (!vista && tamano.ancho) encuadrar(foco || { x: 0, y: 0, ancho: mapa.ancho, alto: mapa.alto });
  }, [vista, tamano, encuadrar, foco, mapa]);

  const zoomEn = (factor, punto) => {
    setVista((v) => {
      const escala = limitar(v.escala * factor, ESCALA_MIN, ESCALA_MAX);
      const centro = punto || { x: tamano.ancho / 2, y: tamano.alto / 2 };
      const enPlano = { x: (centro.x - v.x) / v.escala, y: (centro.y - v.y) / v.escala };
      return { escala, x: centro.x - enPlano.x * escala, y: centro.y - enPlano.y * escala };
    });
  };

  const alGirarRueda = (e) => {
    e.evt.preventDefault();
    zoomEn(Math.exp(-e.evt.deltaY * 0.002), e.target.getStage().getPointerPosition());
  };

  const toques = (e) => {
    const t = e.evt.touches;
    if (t.length !== 2) {
      pellizco.current = null;
      return;
    }
    e.evt.preventDefault();
    const rect = contenedorRef.current.getBoundingClientRect();
    const centro = { x: (t[0].clientX + t[1].clientX) / 2 - rect.left, y: (t[0].clientY + t[1].clientY) / 2 - rect.top };
    const distancia = Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    if (pellizco.current) zoomEn(distancia / pellizco.current, centro);
    pellizco.current = distancia;
  };

  const detalle = vista && vista.escala >= ESCALA_NUMEROS;

  return (
    <Box ref={contenedorRef} sx={{ position: "relative", flex: 1, minHeight: 0, bgcolor: COLOR.fondoLienzo, overflow: "hidden", touchAction: "none" }}>
      {vista && (
        <Stage
          width={tamano.ancho}
          height={tamano.alto}
          x={vista.x}
          y={vista.y}
          scaleX={vista.escala}
          scaleY={vista.escala}
          draggable
          onDragEnd={(e) => {
            if (e.target === e.target.getStage()) setVista((v) => ({ ...v, x: e.target.x(), y: e.target.y() }));
          }}
          onWheel={alGirarRueda}
          onTouchMove={toques}
          onTouchEnd={() => (pellizco.current = null)}
        >
          <Layer>
            <Rect width={mapa.ancho} height={mapa.alto} fill={COLOR.plano} cornerRadius={12} listening={false} />
            {mapa.decorativos.map((d, i) => (
              <DecorativoFigura key={i} decorativo={d} />
            ))}
            {mapa.sectores
              .filter((s) => s.en_mapa)
              .map((s) => (
                <Sector
                  key={s.id}
                  sector={s}
                  color={colorDeUbicacion(s, colores)}
                  activo={activos.has(s.id)}
                  resaltado={resaltado === s.id}
                  elegidas={cantidadGeneral ? cantidadGeneral(s) : 0}
                  onGeneral={onGeneral}
                  detalle={detalle}
                  estadoAsiento={estadoAsiento}
                  mesaEntera={esMesaEntera(s)}
                  estadoMesa={estadoMesa}
                  onAsiento={onAsiento}
                  onMesa={onMesa}
                />
              ))}
          </Layer>
        </Stage>
      )}
      <Box
        sx={{
          position: "absolute",
          right: 12,
          bottom: 12,
          display: "flex",
          flexDirection: "column",
          bgcolor: "#ffffff",
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        }}
      >
        <Tooltip title="Acercar" placement="left">
          <IconButton size="small" onClick={() => zoomEn(1.3)} aria-label="Acercar">
            <Add />
          </IconButton>
        </Tooltip>
        <Tooltip title="Alejar" placement="left">
          <IconButton size="small" onClick={() => zoomEn(1 / 1.3)} aria-label="Alejar">
            <Remove />
          </IconButton>
        </Tooltip>
        <Tooltip title="Ver todo" placement="left">
          <IconButton size="small" onClick={() => encuadrar(todo)} aria-label="Ver todo">
            <CenterFocusStrong />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default PlanoCompra;
